import {z} from 'zod'

import {toApiDateString} from '../jobs/job-schedule-format'

const invoiceLineItemRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    charge_type: z.string(),
    quantity: z.string(),
    unit: z.string(),
    unit_price: z.string(),
    total_price: z.string(),
    job_id: z.string().nullable().optional(),
    job_name: z.string().nullable().optional(),
})

export const invoiceLineItemSchema = invoiceLineItemRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    description: raw.description,
    chargeType: raw.charge_type,
    quantity: Number(raw.quantity),
    unit: raw.unit,
    unitPrice: Number(raw.unit_price),
    totalPrice: Number(raw.total_price),
    jobId: raw.job_id ?? null,
    jobName: raw.job_name ?? null,
}))

export type InvoiceLineItemRaw = z.infer<typeof invoiceLineItemRawSchema>
export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>

const invoiceRawSchema = z.object({
    id: z.string(),
    project_id: z.string(),
    project_name: z.string(),
    line_items: z.array(invoiceLineItemRawSchema),
    total: z.string(),
    is_draft: z.boolean(),
    is_voided: z.boolean(),
    voided_at: z.string().nullable(),
    external_invoice_number: z.string().nullable(),
    date_invoice_issued: z.string().nullable(),
    po_number: z.string().nullable().optional(),
    created_at: z.string(),
})

export const invoiceSchema = invoiceRawSchema.transform((raw) => ({
    id: raw.id,
    projectId: raw.project_id,
    projectName: raw.project_name,
    lineItems: raw.line_items.map((lineItem) => invoiceLineItemSchema.parse(lineItem)),
    total: Number(raw.total),
    isDraft: raw.is_draft,
    isVoided: raw.is_voided,
    voidedAt: raw.voided_at,
    externalInvoiceNumber: raw.external_invoice_number,
    dateInvoiceIssued: raw.date_invoice_issued,
    poNumber: raw.po_number ?? null,
    createdAt: raw.created_at,
}))

export type InvoiceRaw = z.infer<typeof invoiceRawSchema>
export type Invoice = z.infer<typeof invoiceSchema>

const invoiceListItemRawSchema = z.object({
    id: z.string(),
    project_id: z.string(),
    project_name: z.string(),
    line_item_count: z.number(),
    total: z.string(),
    is_draft: z.boolean(),
    is_voided: z.boolean(),
    created_at: z.string(),
    po_number: z.string().nullable().optional(),
})

export const invoiceListItemSchema = invoiceListItemRawSchema.transform((raw) => ({
    id: raw.id,
    projectId: raw.project_id,
    projectName: raw.project_name,
    lineItemCount: raw.line_item_count,
    total: Number(raw.total),
    isDraft: raw.is_draft,
    isVoided: raw.is_voided,
    createdAt: raw.created_at,
    poNumber: raw.po_number ?? null,
}))

export type InvoiceListItemRaw = z.infer<typeof invoiceListItemRawSchema>
export type InvoiceListItem = z.infer<typeof invoiceListItemSchema>

export const createInvoiceInputSchema = z.object({
    projectId: z.string(),
    lineItemIds: z.array(z.string()).min(1),
})

export type CreateInvoiceInput = z.input<typeof createInvoiceInputSchema>

export const createInvoiceRequestSchema = createInvoiceInputSchema.transform(
    ({projectId, lineItemIds}) => ({
        project_id: projectId,
        line_item_ids: lineItemIds,
    }),
)

export type CreateInvoiceRequestRaw = z.infer<typeof createInvoiceRequestSchema>

export const recordExternalInvoiceInputSchema = z.object({
    externalInvoiceNumber: z.string().trim().min(1),
    dateInvoiceIssued: z.date(),
})

export type RecordExternalInvoiceInput = z.infer<typeof recordExternalInvoiceInputSchema>

export const recordExternalInvoiceRequestSchema = recordExternalInvoiceInputSchema.transform(
    ({externalInvoiceNumber, dateInvoiceIssued}) => ({
        external_invoice_number: externalInvoiceNumber,
        date_invoice_issued: toApiDateString(dateInvoiceIssued),
    }),
)

export type RecordExternalInvoiceRequestRaw = z.infer<typeof recordExternalInvoiceRequestSchema>
